// React surface for protocol-client. A single ControllerProvider wraps
// the live ControllerSocket and AuthSession; hooks (useController,
// useAuth, useSubscription, useSnapshot) deliver them to screens.
//
// Screens NEVER touch ControllerSocket directly. They subscribe to the
// envelopes they care about via `useSubscription` (live stream) or
// fetch a one-shot via `useSnapshot` / `useRequest`.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AuthClient, isAuthError, type AuthError, type AuthSession } from './auth';
import {
  ControllerSocket,
  envelopeMatchesPattern,
  type Envelope,
  type SocketOptions,
} from './controller_socket';
import type { Role } from '@usability-suite/schema';

interface ControllerCtx {
  socket: ControllerSocket;
  session: AuthSession | null;
  setSession: (s: AuthSession | null) => void;
  authClient: AuthClient;
  connected: boolean;
  registered: boolean;
}

const Ctx = createContext<ControllerCtx | null>(null);

export interface ControllerProviderProps {
  options: Omit<SocketOptions, 'role' | 'nodeId'> & {
    role: Role;
    /**
     * Stable node id for this tab. Defaults to a session-id-like value
     * generated from the time the provider mounts so two tabs as the
     * same role don't collide.
     */
    nodeId?: string;
  };
  initialSession?: AuthSession | null;
  children: ReactNode;
}

function genNodeId(role: Role): string {
  return `${role}-${Math.random().toString(36).slice(2, 10)}`;
}

export function ControllerProvider({
  options,
  initialSession = null,
  children,
}: ControllerProviderProps) {
  const [session, setSessionState] = useState<AuthSession | null>(initialSession);
  const [connected, setConnected] = useState(false);
  const [registered, setRegistered] = useState(false);

  const socketRef = useRef<ControllerSocket | null>(null);
  const authRef = useRef<AuthClient | null>(null);

  if (!socketRef.current) {
    socketRef.current = new ControllerSocket({
      ...options,
      nodeId: options.nodeId ?? genNodeId(options.role),
    });
    authRef.current = new AuthClient(options.origin ?? '');
    if (initialSession) socketRef.current.setToken(initialSession);
  }

  // The socket pushes a state-change callback whenever connected /
  // registered transitions, so the provider re-renders on the next
  // microtask rather than waiting on a polling timer.
  useEffect(() => {
    const socket = socketRef.current!;
    const sync = () => {
      setConnected(socket.isConnected());
      setRegistered(socket.isRegistered());
    };
    const off = socket.onStateChange(sync);
    socket.start();
    sync();
    // Best-effort: signal a clean close on tab unload so the
    // SharedWorker broker (which can't observe MessagePort death
    // on its own) can drop our node row immediately instead of
    // waiting for a heartbeat timeout that doesn't exist yet. The
    // browser doesn't guarantee a handler runs to completion on
    // pagehide, so this is "try and hope"; the broker is still
    // resilient to never receiving it.
    const onPageHide = () => {
      try {
        socket.stop();
      } catch {
        // ignore — page is going away anyway.
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', onPageHide);
    }
    return () => {
      off();
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', onPageHide);
      }
      socket.stop();
    };
  }, []);

  const setSession = useCallback((s: AuthSession | null) => {
    setSessionState(s);
    if (s) socketRef.current!.setToken(s);
  }, []);

  const value = useMemo<ControllerCtx>(
    () => ({
      socket: socketRef.current!,
      session,
      setSession,
      authClient: authRef.current!,
      connected,
      registered,
    }),
    [session, setSession, connected, registered],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useController(): ControllerCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useController must be inside <ControllerProvider>');
  return v;
}

/**
 * Non-throwing variant of `useController` for components that can
 * sensibly render in either online or offline mode (e.g. Designer's
 * status indicator). Returns null when there is no ControllerProvider
 * above this component.
 */
export function useOptionalController(): ControllerCtx | null {
  return useContext(Ctx);
}

/** Subscribe to a topic pattern; receive every matching inbound envelope. */
export function useSubscription(
  pattern: string,
  onEnvelope: (env: Envelope) => void,
): void {
  const { socket, registered } = useController();
  const handlerRef = useRef(onEnvelope);
  handlerRef.current = onEnvelope;

  useEffect(() => {
    const offSub = socket.subscribe(pattern);
    const offMsg = socket.onMessage((env) => {
      if (envelopeMatchesPattern(env, pattern)) {
        try {
          handlerRef.current(env);
        } catch {
          // listener errors do not break the subscription
        }
      }
    });
    return () => {
      offMsg();
      offSub();
    };
    // `pattern` changing tears down and re-subscribes. `registered`
    // gates not the subscription itself (the socket queues it) but
    // any view that wants to react to "we're live now".
  }, [pattern, socket, registered]);
}

/** Run a one-shot state.get; returns { data, loading, error, reload }. */
export function useSnapshot<T = unknown>(
  resource: string,
  filter: Record<string, unknown> = {},
): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const { socket, registered } = useController();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    if (!registered) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const parsedFilter = JSON.parse(filterKey) as Record<string, unknown>;
    socket
      .request({ type: 'state.get', payload: { resource, filter: parsedFilter } })
      .then((env) => {
        if (cancelled) return;
        const items = (env.payload as { items?: T }).items ?? null;
        setData(items);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [socket, registered, resource, filterKey, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

/** Helpers for callers that want a one-off login flow. */
export async function login(
  client: AuthClient,
  role: Role,
  password: string,
): Promise<AuthSession | AuthError> {
  return client.login(role, password);
}

export { isAuthError };
